import React, { useState, useEffect, useRef } from "react";
import TopNavBar from "../../Custom/TopNavBar.js";
import { Box, Button, Typography } from "@mui/material";
import OfficeImg from "../../Assets/office.jpg";
import TeamList from "../TeamList/TeamList.js";
import { useSpring, animated, useScroll } from "@react-spring/web"; // Import react-spring
import "../../css/animation.css"; // Import the animation file
import Footer from "../FooterSection.js";
import FloatingNavBar from "../../Custom/FloatingNavBar.js";
import http from "../../../../../httpService.js";
import { useParams } from "react-router-dom";
import CustomCursor from "../../Custom/CustomCursor.js";

const PortfolioPage = () => {
  const mainBoxRef = useRef(null);
  const triggerRef = useRef(null);
  const containerRef = React.useRef(null);
  const [boxHeight, setBoxHeight] = useState(window.innerHeight);
  const [isProduct, setIsProduct] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [categoryList, setCategoryList] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFloatingNav, setShowFloatingNav] = useState(false);
  const { uuid } = useParams();
  const [checkDepartment, setCheckDepartment] = useState(0);
  const [hoveringText, setHoveringText] = useState(false);
  const { scrollYProgress } = useScroll({
    container: containerRef,
    // onChange: ({ value }) =>
    //   console.log("Scroll Progress:", value.scrollYProgress), // Log scroll progress
  });


  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        const response = await http.get("/api/portfolio/" + uuid, {
          headers: {
            Accept: "application/json",
            "ngrok-skip-browser-warning": "true",
          },
        });
        sessionStorage.setItem("portolioId", uuid); // Store the uuid in session storage

        if (response.data.success && response.data.data) {
          const apiData = response.data.data;

          let employees = [];
          let categories = [];
          setCheckDepartment(apiData?.department_wise); // Update checkDepartment based on response
          Object.keys(apiData).forEach((key) => {
            const members = apiData[key];
            if (Array.isArray(members)) {
              categories.push(key);
              const enrichedMembers = members.map((member) => ({
                ...member,
                category: key,
              }));
              employees = [...employees, ...enrichedMembers];
            }
          });
          // console.log(employees); // All employees with their respective category

          setCategoryList(["All", ...categories]);
          setTeamMembers(employees);
          console.log('Team Members:', employees); // Log team members for debugging
        } else {
          setTeamMembers([]); // If no valid data, set to empty array
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setTeamMembers([]); // Handle error scenario
      } finally {
        setLoading(false);
      }
    };

    fetchTeamMembers();
  }, []);

  // Update height when component mounts or updates
  useEffect(() => {
    const updateHeight = () => {
      if (mainBoxRef.current) {
        setBoxHeight(mainBoxRef.current.clientHeight);
      }
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  // React Spring Animations

  const descriptionText =
    "ClockHash Technologies is a pioneering IT company that not only provides Team Augmentation and Offshore Development services but also develops its own cutting-edge products. With expertise in Cloud Computing, DevOps, Full Stack Development, AI/ML, and IoT, we drive innovation and deliver scalable technology solutions.";

  // Split the description text into individual words
  const words = descriptionText.split(" ");

  const getWordColors = (scrollProgress) => {
    const totalWords = words.length;

    // Define the min and max scroll values
    const minScroll = 0.22;
    const maxScroll = 0.3;

    // Normalize scrollProgress to range 0 - 1 within the min-max range
    let normalizedProgress =
      (scrollProgress - minScroll) / (maxScroll - minScroll);
    normalizedProgress = Math.max(0, Math.min(1, normalizedProgress)); // Clamp between 0 and 1

    // Calculate the number of words to be white
    const whiteWordsCount = Math.ceil(normalizedProgress * totalWords);

    return words.map((_, index) =>
      index < whiteWordsCount ? "white" : "grey"
    );
  };

  // Product Text Switching Animation
  useEffect(() => {
    const interval = setInterval(() => {
      setIsProduct((prev) => !prev);
    }, 2000);
    return () => clearInterval(interval);
  }, []);
  const productFadeAnimation = useSpring({
    opacity: 1,
    from: { opacity: 0 },
    reset: true,
    config: { duration: 100 },
    // Trigger on value change
    key: isProduct ? "Product" : "Services",
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && checkDepartment === 1) {
          setShowFloatingNav(true); // Show navbar when the section is in view and department_wise is 1
        } else {
          setShowFloatingNav(false); // Hide navbar if not in view
        }
      },
      {
        root: null, // viewport
        threshold: 0.4, // 60% of the element should be visible before it triggers
      }
    );

    if (triggerRef.current) {
      observer.observe(triggerRef.current); // Start observing the trigger element
    }

    return () => {
      if (triggerRef.current) {
        observer.unobserve(triggerRef.current); // Cleanup the observer
      }
    };
  }, [checkDepartment]);

  // Linked Scroll Animation using React Spring
  const fadeAnimation = useSpring({
    opacity: scrollYProgress.to([0, 0.029], [1, 0]), // Fade out as you scroll
    config: { duration: 500 },
  });

  const moveDownAnimation = useSpring({
    transform: scrollYProgress.to(
      [0, 0.029, 0.058, 0.087, 0],
      [
        "translateY(0px) translateX(0px) scale(1)",
        "translateY(50px) translateX(-100px) scale(0.8)",
        "translateY(100px) translateX(-200px) scale(0.7)",
        "translateY(350px) translateX(-380px) scale(0.6)",
        "translateY(350px) translateX(-380px) scale(0.6)",
      ]
    ),
    config: { duration: 5 },
  });

  const weAreTextAnimation = useSpring({
    opacity: scrollYProgress.to([0.09, 0.1], [0, 1]), // Fade in as you scroll
    transform: scrollYProgress.to(
      [0.09, 0.1],
      [
        "translateY(250px) translateX(-240px)",
        "translateY(250px) translateX(-240px)",
      ]
    ),
    config: { duration: 10 },
  });

  const aProductAnimation = useSpring({
    opacity: scrollYProgress.to([0.11, 0.12], [0, 1]), // Fade in as you scroll further
    transform: scrollYProgress.to(
      [0.1, 0.12, 0],
      [
        "translateY(310px) translateX(-300px)",
        "translateY(310px) translateX(-239px) ",
        "translateY(310px) translateX(-239px)",
      ]
    ),
    config: { duration: 2 },
  });

  const ProviderAnimation = useSpring({
    opacity: scrollYProgress.to([0.12, 0.17], [0, 1]), // Fade in as you scroll further
    transform: scrollYProgress.to(
      [0.12, 0.17, 0],
      [
        "translateY(393px) translateX(-300px)",
        "translateY(393px) translateX(-239px) ",
        "translateY(393px) translateX(-239px)",
      ]
    ),
    config: { duration: 10 },
  });

  const descriptionBoxAnimation = useSpring({
    // color: scrollYProgress.to([0.17, 0.3], [getWordColors(0),getWordColors(100)]), // Change color from grey to white
    // opacity: scrollYProgress.to([0.13, 0.17], [0, 1]), // Fade in as you scroll further
    transform: scrollYProgress.to(
      [0.12, 0.17, 0],
      [
        "translateY(380px) translateX(430px)",
        "translateY(380px) translateX(430px) ",
        "translateY(380px) translateX(430px)",
      ]
    ),
    config: { duration: 2 },
  });

  const fadeInBottomNavAnimation = useSpring({
    opacity: scrollYProgress.to([0.4, 0.4, 0.78, 0.78], [0, 1, 1, 0]), // Fade in as you scroll further
    config: { duration: 10 },
  });

  return (
    <>
      <Box
        sx={{
          position: "relative",
          p: 2,
          minHeight: "100vh", // Minimum height to cover the entire viewport
          backgroundImage: `linear-gradient(to top, rgba(0, 0, 0, .97)12%, rgba(0, 0, 0, 0.25) 35%), 
             linear-gradient(to bottom, rgba(0, 0, 0, 1) 5%, rgba(0, 0, 0, 0.25) 35%), 
             url(${OfficeImg})`,
          backgroundSize: "150% 625px", // Fix the height of the background image and make it responsive
          backgroundPosition: "center",
          backgroundRepeat: "repeat-y", // Repeat background on the Y-axis if content exceeds height
          width: "100%", // Full width
          // overflow: "auto", // Allow scrolling if needed
          animation: "backgroundMove 10s infinite linear", // Apply background animation
        }}
      >
        <CustomCursor isHovering={hoveringText} />
        <TopNavBar uuid={uuid} />
        {showFloatingNav && checkDepartment === 1 && (
          <animated.div style={fadeInBottomNavAnimation}>
            <FloatingNavBar
              categoryList={categoryList}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
            />
          </animated.div>
        )}

        {/* Content Box */}
        <Box
          ref={mainBoxRef}
          sx={{
            position: "relative",
            backgroundColor: "rgba(126, 126, 126, 0.2)",
            borderRadius: "24px",
            minHeight: "100vh",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            backdropFilter: "blur(3px)",
            border: "1px solid rgba(234, 234, 234, 0.2)",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.5)", // Added shadow
            zIndex: 2,
            overflow: "hidden",
            // mb: 1.2,
          }}
        >
          {/* Welcome Section with Animation */}
          {/* <animated.div style={welcomeAnimation}> */}
          <Box
            sx={{
              height: "850px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 2,
              mt: 42,
              mb: 10,
            }}
          >
            <Box>
              <animated.div style={fadeAnimation}>
                <Typography
                  sx={{
                    fontFamily: "Poppins, sans-serif",
                    color: "#EBEBEB",
                    fontSize: "1.4rem",
                    fontWeight: "300",
                    textAlign: "start",
                  }}
                >
                  Welcome!
                </Typography>
              </animated.div>
              <animated.div style={weAreTextAnimation}>
                <Typography
                  sx={{
                    fontFamily: "Poppins, sans-serif",
                    color: "#FFFFFF",
                    fontSize: "5.8rem",
                    fontWeight: "500",
                    position: "absolute",
                    top: "10%",
                  }}
                >
                  We're
                </Typography>
              </animated.div>

              <animated.div style={moveDownAnimation}>
                <Typography
                  onMouseEnter={() => setHoveringText(true)}
                  onMouseLeave={() => setHoveringText(false)}
                  sx={{
                    fontFamily: "Poppins, sans-serif",
                    color: "#FFFFFF",
                    fontSize: "8rem",
                    fontWeight: "600",
                    lineHeight: 1,
                  }}
                >
                  Clockhash
                </Typography>
              </animated.div>
              <animated.div style={aProductAnimation}>
                <Typography
                  sx={{
                    fontFamily: "Prata, sans-serif",
                    fontSize: "5.3rem",
                    fontWeight: "500",
                    position: "absolute",
                    top: "10%",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <span style={{ color: "#FFFFFF" }}>a</span>
                  <animated.span
                    style={{ color: "#000000", ...productFadeAnimation }}
                  >
                    {isProduct ? "Product" : "Services"}
                  </animated.span>
                </Typography>
              </animated.div>

              <animated.div style={ProviderAnimation}>
                <Typography
                  sx={{
                    fontFamily: "Poppins, sans-serif",
                    color: "#FFFFFF",
                    fontSize: "5.3rem",
                    fontWeight: "500",
                    position: "absolute",
                    top: "10%",
                  }}
                >
                  Provider
                </Typography>
              </animated.div>
              <animated.div style={fadeAnimation}>
                <Typography
                  sx={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: "175",
                    color: "#EBEBEB",
                    fontSize: "1.8rem",
                    textAlign: "end",
                    lineHeight: 1.3,
                  }}
                >
                  Technologies
                </Typography>
              </animated.div>
              <animated.div style={fadeAnimation}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    width: "100%",
                    mt: 4,
                    pt: 5,
                  }}
                >
                  <Typography
                    sx={{
                      textAlign: "center",
                      fontFamily: "Poppins, sans-serif",
                      color: "#D3D3D3",
                      fontWeight: 400,
                      fontSize: "1.1rem",
                    }}
                  >
                    Bringing bold ideas to life through
                  </Typography>
                  <Typography
                    sx={{
                      textAlign: "center",
                      fontFamily: "Prata, sans-serif",
                      color: "#D3D3D3",
                      fontWeight: 400,
                      letterSpacing: 1,
                      fontSize: "1.1rem",
                    }}
                  >
                    design and tech
                  </Typography>
                </Box>
              </animated.div>
              <animated.div style={descriptionBoxAnimation}>
                <Box
                  sx={{
                    position: "absolute",
                    width: "72%",
                    padding: 3,
                    color: "white",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "1.3rem",
                      fontFamily: "Poppins",
                      fontWeight: "300",
                      lineHeight: 1.5,
                    }}
                  >
                    {words.map((word, index) => (
                      <animated.span
                        key={index}
                        style={{
                          color: scrollYProgress.to(
                            (scrollProgress) =>
                              getWordColors(scrollProgress)[index]
                          ), // Dynamically calculate the color for each word
                        }}
                      >
                        {word}{" "}
                      </animated.span>
                    ))}
                  </Typography>
                </Box>
              </animated.div>
            </Box>
            <Box
              sx={{
                textAlign: "center",
                mt: 80,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "40%",
              }}
            ></Box>
            <Typography
              sx={{
                color: "#FFFFFF",
                mt: 8,
                fontSize: "4.9rem",
                fontFamily: "Poppins, sans-serif",
                fontWeight: "600",
              }}
            >
              Meet the Team
            </Typography>
          </Box>

          {/* </animated.div> */}

          {/* Team List Section with Animation */}
          <Box
            ref={triggerRef}
            sx={{
              height: "100%",
              width: "96%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              zIndex: 2,
              mt: 9,
              overflowY: "auto",
            }}
          >
            <TeamList
              selectedCategory={selectedCategory}
              teamMembers={teamMembers}
              loading={loading}
            />
          </Box>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "left",
              alignItems: "start",
              textAlign: "start",
              zIndex: 2,
              pl: 10,
              mt: 15,
              mb: 10,
              color: "white",
            }}
          >
            <Box sx={{ marginBottom: 3 }}>
              <Typography
                sx={{
                  fontSize: "1.7rem",
                  fontFamily: "Poppins, sans-serif",
                  fontWeight: "100",
                }}
              >
                Make an
              </Typography>
              <Typography
                sx={{
                  fontSize: "7.5rem",
                  fontWeight: "700",
                  fontFamily: "Poppins, sans-serif",
                  lineHeight: 1,
                }}
              >
                Impact
              </Typography>
              <Typography
                sx={{
                  fontSize: "1.4rem",
                  textAlign: "end",
                  fontFamily: "Poppins, sans-serif",
                  fontWeight: "100",
                }}
              >
                With us!
              </Typography>
            </Box>
            <Box sx={{ width: "30%" }}>
              <Typography
                sx={{
                  fontSize: "1.9rem",
                  fontFamily: "Poppins, sans-serif",
                  fontWeight: "400",
                  lineHeight: 1.2,
                  marginBottom: 2,
                }}
              >
                Trial Period as a part of Hiring process
              </Typography>
              <Typography
                sx={{
                  fontSize: "1rem",
                  fontWeight: "100",
                  fontFamily: "Poppins, sans-serif",
                  lineHeight: 1.2,
                }}
              >
                We offer 3 days trial for clients to prove our candidates are a
                good match
              </Typography>
              <Button
                sx={{
                  borderRadius: "12px",
                  backgroundColor: "rgba(71, 71, 71, 0.2)",
                  backdropFilter: "blur(20px)",
                  color: "white",
                  marginTop: 2,
                  paddingX: 1.5,
                  paddingY: 1,
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "1.2rem",
                  fontWeight: "300",
                  textTransform: "inherit", // Capitalize the text
                  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.5)", // Added shadow
                  border: "1px solid rgba(34, 34, 34, 0.3)", // Optional border
                  transition: "all 0.3s ease", // Smooth transition for hover effect
                  "&:hover": {
                    color: "black",
                    boxShadow: "0px 6px 15px rgba(0, 0, 0, 0.7)", // Stronger shadow on hover
                    backgroundColor: "rgba(179, 177, 177, 0.4)", // Slightly darker background on hover
                  },
                }}
                onClick={() =>
                  window.open(
                    "https://calendly.com/marketing-clockhash/introduction-call",
                    "_blank"
                  )
                }
              >
                Book an Appointment
              </Button>
            </Box>
          </Box>
          <Footer />
          <Box sx={{ zIndex: 2, mb: 0.5 }}>
            <Typography sx={{ color: "white", fontSize: ".6rem" }}>
              @ Copyright 2025 Clockhash.All Rights Reserved
            </Typography>
          </Box>
        </Box>
        {/* </Box> */}
      </Box>
    </>
  );
};

export default PortfolioPage;
